import { CELLS, ROLES, type CellScope, type PermissionCell, type Role, type ScopeKind } from "./permissions.generated.js";

export { CELLS, ROLES };
export type { CellScope, PermissionCell, Role, ScopeKind };

/**
 * The permission matrix as code (permission-matrix.md §6, SEC-04): the
 * cells are generated from the document; these are the only questions
 * the authorisation layer and the UI ask of them.
 */
export const ROLE_NAMES = Object.keys(ROLES) as Role[];

export function isRole(value: string): value is Role {
  return value in ROLES;
}

/** `resource.action` as a route declares it. */
export type Permission = `${string}.${string}`;

export function permissionOf(cell: Pick<PermissionCell, "resource" | "action">): Permission {
  return `${cell.resource}.${cell.action}`;
}

const byPermission = new Map<string, PermissionCell[]>();
for (const cell of CELLS) {
  const key = permissionOf(cell);
  const list = byPermission.get(key);
  if (list) list.push(cell);
  else byPermission.set(key, [cell]);
}

/** Every `resource.action` the matrix names — a route declaring another fails at startup. */
export const PERMISSIONS: readonly Permission[] = [...byPermission.keys()] as Permission[];

/** The cells a permission has, one per role that may hold it. */
export function cellsOf(permission: string): readonly PermissionCell[] {
  return byPermission.get(permission) ?? [];
}

/** The cell for one role on one permission, if the matrix has it. */
export function cellFor(permission: string, role: Role): PermissionCell | undefined {
  return cellsOf(permission).find((c) => c.role === role);
}

/** The permissions a set of roles reaches, for `/v1/me.permissions` (permission-matrix.md §6). */
export function permissionsForRoles(roles: readonly Role[]): Permission[] {
  const set = new Set<Permission>();
  for (const cell of CELLS) if (roles.includes(cell.role)) set.add(permissionOf(cell));
  return [...set].sort();
}

/**
 * The roles a grant may carry (permission-matrix.md §5): learner is
 * implicit in membership, applicant is a token, apprentice is the system's
 * at conversion, mentor is a mentor_assignment, platform-operator is
 * membership of __platform. What is left is what role_grant holds.
 */
export const GRANTABLE_ROLES = ["instructor", "program-director", "compliance-admin", "tenant-admin", "employer", "auditor"] as const satisfies readonly Role[];
export type GrantableRole = (typeof GRANTABLE_ROLES)[number];

export function isGrantableRole(value: string): value is GrantableRole {
  return (GRANTABLE_ROLES as readonly string[]).includes(value);
}

/** Who may confer which role (permission-matrix.md §5). */
export const GRANTORS: Readonly<Record<GrantableRole, readonly Role[]>> = {
  instructor: ["program-director"],
  "program-director": ["tenant-admin"],
  "compliance-admin": ["tenant-admin"],
  "tenant-admin": ["tenant-admin"],
  employer: ["program-director"],
  auditor: ["compliance-admin"],
};

/** The auditor's time box (permission-matrix.md §1, §10 D1). */
export const AUDITOR_DEFAULT_DAYS = 30;
export const AUDITOR_MAX_DAYS = 90;

/** Grants whose change signs the person out everywhere (permission-matrix.md §4.7, §10 D7). */
export const SIGN_OUT_ON_CHANGE: readonly Role[] = ["program-director", "compliance-admin", "tenant-admin", "auditor"];
